import { supabase } from '../lib/supabase';

export interface UserSearchResult {
  id: string;
  alias: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  created_at: string;
}

export class UserService {
  // Mock sellers for demonstration (in real app, these would come from database)
  private static mockSellers: UserSearchResult[] = [
    {
      id: 'seller-1',
      alias: 'techstore_pro',
      first_name: 'John',
      last_name: 'Smith',
      email: 'john@techstore.com',
      created_at: '2024-01-15T10:00:00Z'
    },
    {
      id: 'seller-2',
      alias: 'fashionista',
      first_name: 'Sarah',
      last_name: 'Johnson',
      email: 'sarah@fashionista.com',
      created_at: '2024-01-20T14:30:00Z'
    },
    {
      id: 'seller-3',
      alias: 'home_garden_expert',
      first_name: 'Mike',
      last_name: 'Wilson',
      email: 'mike@homegarden.com',
      created_at: '2024-02-01T09:15:00Z'
    },
    {
      id: 'seller-4',
      alias: 'sports_gear',
      first_name: 'Alex',
      last_name: 'Brown',
      email: 'alex@sportsgear.com',
      created_at: '2024-02-10T16:45:00Z'
    },
    {
      id: 'seller-5',
      alias: 'bookworm',
      first_name: 'Emma',
      last_name: 'Davis',
      email: 'emma@bookworm.com',
      created_at: '2024-02-15T11:20:00Z'
    },
    {
      id: 'seller-6',
      alias: 'beauty_queen',
      first_name: 'Lisa',
      last_name: 'Garcia',
      email: 'lisa@beautyqueen.com',
      created_at: '2024-02-20T13:10:00Z'
    },
    {
      id: 'seller-7',
      alias: 'auto_parts_pro',
      first_name: 'Tom',
      last_name: 'Martinez',
      email: 'tom@autoparts.com',
      created_at: '2024-03-01T08:30:00Z'
    },
    {
      id: 'seller-8',
      alias: 'toy_master',
      first_name: 'Chris',
      last_name: 'Anderson',
      email: 'chris@toymaster.com',
      created_at: '2024-03-05T15:00:00Z'
    }
  ];

  /**
   * Search users by alias (for seller filtering)
   */
  static async searchUsersByAlias(query: string): Promise<UserSearchResult[]> {
    try {
      if (!query || query.trim().length < 2) {
        return [];
      }

      console.log('UserService: Searching users by alias:', query);
      
      // For now, use mock data. In production, this would query the database
      const filteredSellers = this.mockSellers.filter(seller => 
        seller.alias.toLowerCase().includes(query.toLowerCase()) ||
        (seller.first_name && seller.first_name.toLowerCase().includes(query.toLowerCase())) ||
        (seller.last_name && seller.last_name.toLowerCase().includes(query.toLowerCase()))
      );

      console.log('UserService: Found users:', filteredSellers.length);
      return filteredSellers;
    } catch (error) {
      console.error('UserService: Error searching users:', error);
      throw error;
    }
  }

  /**
   * Get user by alias (exact match)
   */
  static async getUserByAlias(alias: string): Promise<UserSearchResult | null> {
    try {
      if (!alias || alias.trim().length === 0) {
        return null;
      }

      console.log('UserService: Getting user by alias:', alias);
      
      // For now, use mock data. In production, this would query the database
      const user = this.mockSellers.find(seller => 
        seller.alias.toLowerCase() === alias.toLowerCase()
      );

      console.log('UserService: Found user:', user);
      return user || null;
    } catch (error) {
      console.error('UserService: Error getting user by alias:', error);
      return null;
    }
  }

  /**
   * Get user by ID
   */
  static async getUserById(id: string): Promise<UserSearchResult | null> {
    try {
      if (!id) {
        return null;
      }

      console.log('UserService: Getting user by ID:', id);
      
      // For now, use mock data. In production, this would query the database
      const user = this.mockSellers.find(seller => seller.id === id);

      console.log('UserService: Found user:', user);
      return user || null;
    } catch (error) {
      console.error('UserService: Error getting user by ID:', error);
      return null;
    }
  }

  /**
   * Get current user's alias from Supabase
   */
  static async getCurrentUserAlias(userId: string): Promise<string | null> {
    try {
      if (!userId) {
        return null;
      }

      console.log('UserService: Getting current user alias for ID:', userId);
      
      const { data, error } = await supabase
        .from('users')
        .select('alias')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('UserService: Error fetching user alias:', error);
        return null;
      }

      console.log('UserService: Found user alias:', data?.alias);
      return data?.alias || null;
    } catch (error) {
      console.error('UserService: Error getting current user alias:', error);
      return null;
    }
  }

  /**
   * Get user alias info including last changed date
   */
  static async getUserAliasInfo(userId: string): Promise<{
    alias: string | null;
    alias_last_changed: string | null;
    can_change: boolean;
  } | null> {
    try {
      if (!userId) {
        return null;
      }

      console.log('UserService: Getting user alias info for ID:', userId);
      
      const { data, error } = await supabase
        .from('users')
        .select('alias, alias_last_changed')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('UserService: Error fetching user alias info:', error);
        return null;
      }

      // Check if user can change alias (not changed in last 30 days)
      const canChange = !data.alias_last_changed || 
        new Date(data.alias_last_changed) < new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);

      console.log('UserService: Found user alias info:', { 
        alias: data?.alias, 
        alias_last_changed: data?.alias_last_changed,
        can_change: canChange 
      });

      return {
        alias: data?.alias || null,
        alias_last_changed: data?.alias_last_changed || null,
        can_change: canChange
      };
    } catch (error) {
      console.error('UserService: Error getting user alias info:', error);
      return null;
    }
  }

  /**
   * Update user alias using the database function
   */
  static async updateUserAlias(userId: string, newAlias: string): Promise<{
    success: boolean;
    error?: string;
    can_change_again?: string;
  }> {
    try {
      if (!userId || !newAlias) {
        return { success: false, error: 'User ID and alias are required' };
      }

      console.log('UserService: Updating user alias:', { userId, newAlias });
      
      const { data, error } = await supabase.rpc('update_user_alias', {
        user_id: userId,
        new_alias: newAlias
      });

      if (error) {
        console.error('UserService: Error updating user alias:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to update alias' 
        };
      }

      // Get the next available change date
      const aliasInfo = await this.getUserAliasInfo(userId);
      const nextChangeDate = aliasInfo?.alias_last_changed ? 
        new Date(new Date(aliasInfo.alias_last_changed).getTime() + 30 * 24 * 60 * 60 * 1000) :
        null;

      console.log('UserService: Successfully updated user alias');
      return { 
        success: true,
        can_change_again: nextChangeDate?.toISOString() || undefined
      };
    } catch (error) {
      console.error('UserService: Error updating user alias:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update alias' 
      };
    }
  }

  /**
   * Check if user can change their alias
   */
  static async canChangeAlias(userId: string): Promise<boolean> {
    try {
      if (!userId) {
        return false;
      }

      console.log('UserService: Checking if user can change alias for ID:', userId);
      
      const { data, error } = await supabase.rpc('can_change_alias', {
        user_id: userId
      });

      if (error) {
        console.error('UserService: Error checking alias change eligibility:', error);
        return false;
      }

      console.log('UserService: Can change alias:', data);
      return data || false;
    } catch (error) {
      console.error('UserService: Error checking alias change eligibility:', error);
      return false;
    }
  }

  /**
   * Get user profile data
   */
  static async getUserProfile(userId: string): Promise<any> {
    try {
      if (!userId) {
        console.log('❌ UserService: No userId provided');
        return null;
      }

      console.log('🔄 UserService: Getting user profile for ID:', userId);
      
      const { data, error } = await supabase.rpc('get_user_profile', {
        user_id: userId
      });

      if (error) {
        console.error('❌ UserService: RPC function failed, trying direct table query:', error);
        console.error('❌ Error details:', error.message, error.details, error.hint);
        
        // Fallback: Query users table directly
        console.log('🔄 UserService: Trying direct users table query...');
        const { data: directData, error: directError } = await supabase
          .from('users')
          .select('id, email, first_name, last_name, phone, website, social_media_links, bio, location, interests, is_public, notifications_enabled, alias, work_experience, education, created_at, updated_at')
          .eq('id', userId)
          .single();
          
        if (directError) {
          console.error('❌ UserService: Direct query also failed:', directError);
          return null;
        }
        
        console.log('✅ UserService: Direct query successful:', directData);
        return directData;
      }

      console.log('✅ UserService: Found user profile:', data);
      // RPC function returns an array, so we need to get the first element
      return data && data.length > 0 ? data[0] : null;
    } catch (error) {
      console.error('❌ UserService: Exception getting user profile:', error);
      return null;
    }
  }

  /**
   * Update user profile
   */
  static async updateUserProfile(userId: string, profileData: any): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      if (!userId || !profileData) {
        return { success: false, error: 'User ID and profile data are required' };
      }

      console.log('UserService: Updating user profile:', { userId, profileData });
      
      const { data, error } = await supabase.rpc('update_user_profile', {
        user_id: userId,
        profile_data: profileData
      });

      if (error) {
        console.error('UserService: Error updating user profile:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to update profile' 
        };
      }

      console.log('UserService: Successfully updated user profile');
      return { success: true };
    } catch (error) {
      console.error('UserService: Error updating user profile:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update profile' 
      };
    }
  }

  /**
   * Update user profile using direct table update
   */
  static async updateUserProfileDirect(userId: string, updates: any): Promise<{
    success: boolean;
    error?: string;
  }> {
    try {
      if (!userId || !updates) {
        return { success: false, error: 'User ID and updates are required' };
      }

      console.log('UserService: Updating user profile directly:', { userId, updates });
      
      const { data, error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', userId)
        .select();

      if (error) {
        console.error('UserService: Error updating user profile:', error);
        return { 
          success: false, 
          error: error.message || 'Failed to update profile' 
        };
      }

      console.log('UserService: Successfully updated user profile');
      return { success: true };
    } catch (error) {
      console.error('UserService: Error updating user profile:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update profile' 
      };
    }
  }
}
